import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiCode, FiGithub, FiExternalLink, FiStar, FiGitCommit, FiCalendar, FiCpu, FiLayers } from 'react-icons/fi';
import { Helmet } from 'react-helmet-async';
import { TypeAnimation } from 'react-type-animation';
import toast, { Toaster } from 'react-hot-toast';
import api from '../utils/api.js';
import Beams from '../components/Beams';
const Home = () => {
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [skills, setSkills] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const githubToken = import.meta.env.VITE_GITHUB_TOKEN;
        const [projectsRes, skillsRes, githubRes] = await Promise.all([
          api.get('/api/projects/featured'),
          api.get('/api/skills'),
          fetch('https://api.github.com/users/MrGranday/events?per_page=100', {
            headers: githubToken ? { Authorization: `token ${githubToken}` } : {},
          }).then(res => res.json()),
        ]);

        // Filter contribution events and limit to 4
        const contributionEvents = githubRes
          .filter(event => ['PushEvent', 'CreateEvent', 'PullRequestEvent', 'WatchEvent', 'ForkEvent'].includes(event.type))
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) // newest first
          .slice(0, 6);
        // Fetch repository languages for each event
        const activityWithLanguages = await Promise.all(
          contributionEvents.map(async (event) => {
            try {
              const repoRes = await fetch(`https://api.github.com/repos/${event.repo.name}`, {
                headers: githubToken ? { Authorization: `token ${githubToken}` } : {},
              });
              const repoData = await repoRes.json();
              return { ...event, language: repoData.language || 'Unknown' };
            } catch (error) {
              console.error(`Error fetching repo data for ${event.repo.name}:`, error);
              return { ...event, language: 'Unknown' };
            }
          })
        );

        setFeaturedProjects(projectsRes.data);
        setSkills(skillsRes.data);
        setActivity(activityWithLanguages);
      } catch (error) {
        console.error('Error fetching data:', error);
        setFeaturedProjects([]);
        setSkills([]);
        setActivity([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Add HubSpot tracking code dynamically
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'hs-script-loader';
    script.async = true;
    script.defer = true;
    script.src = '//js-na2.hs-scripts.com/243276710.js';
    document.body.appendChild(script);

    // Cleanup to remove the script when component unmounts
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const hireMe = () => {
    window.scrollTo({ top: document.getElementById('contact')?.offsetTop || 0, behavior: 'smooth' });
    toast.success('Let’s connect!', { position: 'top-right', style: { background: '#10B981', color: '#fff' } });
  };

  return (
    <>
      <Helmet>
        <title>Osman Ghani Granday - Software Engineer</title>
        <meta name="description" content="Full-stack software engineer specializing in modern web technologies and Solana blockchain DeFi applications with Rust and JavaScript." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <Toaster />
        <div className="absolute w-full h-full top-[0px] z-0">
          <Beams
            beamWidth={2}
            beamHeight={30}
            beamNumber={12}
            lightColor="#ffffff"
            speed={2}
            noiseIntensity={1.75}
            scale={0.2}
            rotation={50}
          />
        </div>
        

        <div className="container-custom text-center z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative z-10">
              <TypeAnimation
                sequence={[
                  'Osman Ghani Granday',
                  2000,
                  // 'オスマン・ガニ・グランデイ',
                   'Software Developer',
                  2000,
                  // 'عثمان غني ګرنډي',
                  'Rust Developer',
                  2000,
                  // '奥斯曼·加尼·格兰代',
                  'DevOps Engineer',
                  2000,
                  // '오스만 가니 그란데이',
                  'Blockchain Developer',
                  2000,
                  // 'Осман Гани Грандай',
                  // 'Rust Developer',
                  // 2000,
                ]}
                wrapper="h1"
                speed={50}
                className="text-5xl md:text-7xl font-bold mb-6 text-white"
                repeat={Infinity}
              />
              <motion.p
                className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto text-gray-600 dark:text-gray-300"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Building scalable backend and AI-powered web applications using JavaScript, Python, and Rust. Exploring Solana-based Web3 systems and intelligent automation for the decentralized future.
              </motion.p>
              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <Link to="/projects" className="btn-primary inline-flex items-center">
                  Explore Projects <FiArrowRight className="ml-2" />
                </Link>
                <button onClick={hireMe} className="btn-secondary inline-flex items-center">
                  Hire Me <FiStar className="ml-2" />
                </button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Featured Projects */}
      <section className="section-padding bg-dark-900 relative">
 <img
  src="/2pg1.webp"
  alt="Decorative SVG"
  className="absolute inset-0 m-auto w-[2300px] h-[1300px] object-cover z-[0]"
/>




        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900 dark:text-gray-100">
              Featured Projects
            </h2>
            {loading ? (
              <div className="text-center">Loading...</div>
            ) : featuredProjects.length === 0 ? (
              <div className="text-center text-gray-600 dark:text-gray-300">No featured projects available</div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {featuredProjects.map((project, index) => (
                  <FeaturedProject
                    key={project._id}
                    title={project.title}
                    description={project.description}
                    image={project.image}
                    tech={project.technologies}
                    liveUrl={project.liveUrl}
                    githubUrl={project.githubUrl}
                    index={index}
                  />
                ))}
              </div>
            )}
            <div className="text-center mt-12">
              <Link to="/projects" className="btn-primary">
                View All Projects
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contribution Activity */}
      <section className="section-padding bg-dark-900 ">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900 dark:text-gray-100">
              Contribution Activity
            </h2>
            {loading ? (
              <div className="text-center">Loading...</div>
            ) : activity.length === 0 ? (
              <div className="text-center text-gray-600 dark:text-gray-300">No activity available</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {activity.map((activityItem, index) => {
                  let commitMessage = '';
                  const repoName = activityItem.repo.name.split('/')[1] || activityItem.repo.name;
                  const payload = activityItem.payload || {};

                  // Enhanced extraction logic with better fallbacks
                  switch (activityItem.type) {
                    case 'PushEvent':
                      if (payload.commits && payload.commits.length > 0) {
                        commitMessage = payload.commits[0].message;
                      } else {
                        commitMessage = `Pushed updates to ${repoName}`;
                      }
                      break;
                    case 'CreateEvent':
                      const type = payload.ref_type === 'repository' ? 'repository' : 'branch';
                      commitMessage = `Created ${type} ${payload.ref || repoName}`;
                      break;
                    case 'PullRequestEvent':
                      const action = payload.action ? payload.action.charAt(0).toUpperCase() + payload.action.slice(1) : 'Updated';
                      commitMessage = `${action} pull request in ${repoName}`;
                      break;
                    case 'WatchEvent':
                      commitMessage = `Starred ${repoName}`;
                      break;
                    case 'ForkEvent':
                      commitMessage = `Forked ${repoName}`;
                      break;
                    default:
                      commitMessage = `Activity in ${repoName}`;
                  }

                  return (
                    <ActivityCard
                      key={activityItem.id}
                      index={index}
                      repo={activityItem.repo.name}
                      message={commitMessage}
                      date={activityItem.created_at}
                      language={activityItem.language}
                    />
                  );
                })}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Skills Overview */}
      <section className="section-padding bg-dark-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900 dark:text-gray-100">
              Technical Skills
            </h2>
            {loading ? (
              <div className="text-center">Loading...</div>
            ) : skills.length === 0 ? (
              <div className="text-center text-gray-600 dark:text-gray-300">No skills available</div>
            ) : (
              <div className="grid md:grid-cols-3 gap-8">
                {skills.map((skill, index) => (
                  <SkillCard
                    key={skill._id}
                    title={skill.title}
                    skills={skill.skills}
                    index={index}
                  />
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </>
  );
};

const FeaturedProject = ({ title, description, image, tech, liveUrl, githubUrl, index }) => (
  <motion.div
    className="glass-panel rounded-2xl p-6 shadow-2xl transition-all duration-700 hover:shadow-secondary_accent/10 hover:-translate-y-3 overflow-hidden relative group"
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: index * 0.2, ease: "easeOut" }}
    viewport={{ once: true }}
  >
    {/* Animated background glow */}
    <div className="absolute -inset-0.5 bg-gradient-to-r from-secondary_accent/20 to-accent/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
    
    {/* Background Icon Decor */}
    <div className="absolute -right-6 -bottom-6 opacity-[0.05] group-hover:opacity-[0.1] transition-all duration-700 group-hover:scale-125 group-hover:-rotate-12 pointer-events-none z-0">
      <FiLayers size={180} />
    </div>

    <div className="aspect-video mb-6 rounded-xl overflow-hidden relative z-10 border border-white/5">
      <img
        src={image || 'https://via.placeholder.com/400x225'}
        alt={title}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-dark-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-4">
         <span className="text-white text-xs font-medium tracking-widest uppercase">View Project</span>
      </div>
    </div>
    
    <div className="relative z-10">
      <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-secondary_accent transition-colors tracking-tight">{title}</h3>
      <p className="text-gray-400 mb-6 line-clamp-3 text-sm leading-relaxed font-light">{description}</p>
      
      <div className="flex flex-wrap gap-2 mb-8">
        {tech.map((techItem) => (
          <span
            key={techItem}
            className="text-[10px] uppercase tracking-tighter font-bold px-3 py-1 rounded-full bg-white/5 text-gray-400 border border-white/10 group-hover:border-secondary_accent/40 group-hover:text-gray-200 transition-all duration-300"
          >
            {techItem}
          </span>
        ))}
      </div>

      <div className="flex gap-4">
        {liveUrl && (
          <a
            href={liveUrl}
            className="btn-primary flex-1 inline-flex items-center justify-center text-xs py-2.5 px-4 rounded-xl shadow-lg shadow-secondary_accent/10 hover:shadow-secondary_accent/30 transition-all duration-300"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FiExternalLink className="mr-2" /> Live Demo
          </a>
        )}
        {githubUrl && (
          <a
            href={githubUrl}
            className="btn-secondary flex-1 inline-flex items-center justify-center text-xs py-2.5 px-4 rounded-xl border-white/10 hover:border-secondary_accent transition-all duration-300"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FiGithub className="mr-2" /> Code
          </a>
        )}
      </div>
    </div>
  </motion.div>
);

const SkillCard = ({ title, skills, index }) => (
  <motion.div
    className="glass-effect rounded-2xl p-8 border border-white/5 dark:border-white/10 transition-all duration-500 hover:shadow-2xl hover:shadow-secondary_accent/5 hover:-translate-y-2 relative overflow-hidden group"
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    viewport={{ once: true }}
  >
    {/* Background Icon Decor */}
    <div className="absolute -right-4 -bottom-4 opacity-[0.03] group-hover:opacity-[0.08] transition-all duration-500 group-hover:scale-110 group-hover:rotate-12 pointer-events-none">
      <FiCpu size={120} />
    </div>

    <div className="relative z-10 text-center">
      <div className="inline-block p-3 rounded-2xl bg-secondary_accent/10 text-secondary_accent mb-6 group-hover:scale-110 transition-transform duration-500">
        <FiCode size={24} />
      </div>
      <h3 className="text-xl font-bold mb-6 text-white tracking-tight group-hover:text-secondary_accent transition-colors">{title}</h3>
      <div className="flex flex-wrap gap-2 justify-center">
        {skills.map((skill) => (
          <motion.span
            key={skill}
            className="px-4 py-1.5 rounded-full text-xs font-medium bg-white/5 text-gray-400 border border-white/5 hover:border-secondary_accent/30 hover:text-white transition-all duration-300"
            whileHover={{ scale: 1.1, backgroundColor: 'rgba(16, 185, 129, 0.1)' }}
          >
            {skill}
          </motion.span>
        ))}
      </div>
    </div>
  </motion.div>
);

const ActivityCard = ({ repo, message, date, language, index }) => {
  const formattedDate = new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <motion.div
      className="glass-effect rounded-xl p-5 border border-dark-700/50 hover:border-secondary_accent/50 transition-all duration-500 group relative overflow-hidden"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      whileHover={{ y: -5, boxShadow: '0 20px 40px -20px rgba(0,0,0,0.5)' }}
    >
      {/* Background Icon Decor */}
      <div className="absolute -right-4 -bottom-4 opacity-[0.03] group-hover:opacity-[0.07] transition-all duration-500 group-hover:scale-110 group-hover:-rotate-12 pointer-events-none">
        <FiGithub size={120} />
      </div>

      <div className="relative z-10 flex flex-col h-full">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-secondary_accent/10 text-secondary_accent group-hover:bg-secondary_accent group-hover:text-dark-900 transition-colors duration-300">
              <FiGitCommit size={18} />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 group-hover:text-secondary_accent/80 transition-colors">
                {language || 'GitHub Push'}
              </span>
              <span className="text-[10px] text-gray-500 flex items-center gap-1">
                <FiCalendar size={10} /> {formattedDate}
              </span>
            </div>
          </div>
          <a
            href={`https://github.com/${repo}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-500 hover:text-white transition-colors p-1"
          >
            <FiExternalLink size={16} />
          </a>
        </div>

        <h4 className="text-base font-semibold text-gray-200 mb-3 line-clamp-2 group-hover:text-white transition-colors leading-snug">
          {message}
        </h4>

        <div className="mt-auto pt-3 border-t border-dark-700/50 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-gray-400 truncate max-w-[80%]">
            <FiGithub size={12} className="text-secondary_accent/60" />
            <span className="truncate font-medium">{repo.split('/')[1]}</span>
          </div>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-dark-700 text-gray-400 group-hover:bg-secondary_accent/20 group-hover:text-secondary_accent transition-colors">
            {language}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default Home;